import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import FileUpload from '../components/FileUpload';
import FaqAccordion from '../components/FaqAccordion';
import TipsList from '../components/TipsList';
import ShareButtons from '../components/ShareButtons';
import { trackEvent } from '../utils/analytics';
import { downloadFile } from '../utils/download';
import { Download, ArrowRight, RotateCcw, Copy, PlusCircle, Check, XCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; 

export default function MergePdf() {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleMerge = async () => {
    if (files.length < 2) return toast.error('Please select at least 2 PDF files');
    trackEvent('conversion_started', { tool_name: 'Merge PDF' });
    setStatus('processing'); setError(''); setResult(null);

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    try {
      const res = await axios.post('/api/convert/merge', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatus('done');
      setResult(res.data);
      toast.success('PDFs merged successfully!');
    } catch (err) {
      setStatus('error');
      setError((err.response && err.response.data && err.response.data.error) || 'Merge failed');
    }
  };

  const handleReset = () => { setFiles([]); setStatus(null); setResult(null); setError(''); };

  return (
    
    <div>
    
      <Helmet>
  <title>Merge PDF Files Online Free — No Signup | ILoveConvert</title>
  <meta name="description" content="Combine multiple PDF files into one document. Free, fast, no signup, no watermark. Merge up to 20 PDFs at once." />
  <meta name="keywords" content="merge pdf, combine pdf files, join pdf online, merge pdf free, pdf merger, combine pdf online" />
  <link rel="canonical" href="https://iloveconvert.in/merge-pdf" />
  <meta property="og:title" content="Merge PDF Files Online Free — No Signup | ILoveConvert" />
  <meta property="og:description" content="Combine multiple PDF files into one document. Free, fast, no signup, no watermark." />
  <meta property="og:url" content="https://iloveconvert.in/merge-pdf" />
  <meta property="og:type" content="website" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Merge PDF Files Online Free — No Signup" />
  <meta name="twitter:description" content="Combine multiple PDF files into one document. Free, no signup, no watermark." />
</Helmet>

      <section className="hero" style={{ padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><Copy size={44} strokeWidth={1.5} /></div>
        <h1 style={{ fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 8 }}>Merge PDF Files</h1>
        <p>Combine Your PDFs Easily</p>
        <div style={{ maxWidth: 900, margin: '20px auto', padding: '0 20px' }}>
  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
    Merge PDF files online quickly and easily — combine multiple documents into a single file.
  </p>
  <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--text-muted)' }}>
    Just upload your PDFs, arrange them, and click Merge. Your new PDF is ready to download
    in seconds — no software or signup needed.
  </p>
  <a href="#merge-upload" className="btn btn-primary btn-lg" style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8 }}>TRY NOW</a>
</div>
      </section>

      <div className="upload-page" id="merge-upload">
        {!status && (
          <>
            <FileUpload
              onFiles={setFiles}
              multiple={true}
              accept={{ 'application/pdf': ['.pdf'] }}
            />
            {files.length >= 2 && (
              <div style={{ marginTop: 20 }}>
                <button className="btn btn-primary btn-lg" onClick={handleMerge} style={{ width: '100%' }}>
                  <ArrowRight size={18} /> Merge {files.length} PDFs into One
                </button>
              </div>
            )}
            {files.length === 1 && (
              <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 16, fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <PlusCircle size={16} /> Add at least one more PDF to merge
              </p>
            )}
          </>
        )}

        {status === 'processing' && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="spinner" />
            <p style={{ marginTop: 20, fontWeight: 600 }}>Merging your PDFs...</p>
          </div>
        )}

        {status === 'done' && result && (
          <>
            <div className="result-box">
              <div className="check"><Check size={32} strokeWidth={3} /></div>
              <h3>PDFs Merged Successfully!</h3>
              <p>Your combined PDF is ready to download.</p>
              <button onClick={() => downloadFile(result.downloadUrl, result.downloadUrl.split('/').pop(), 'Merge PDF')} className="download-btn">
                <Download size={18} /> Download Merged PDF
              </button>
              <ShareButtons toolLabel="Merge PDF" />
            </div>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={handleReset}><RotateCcw size={16} /> Merge Again</button>
            </div>
          </>
        )}

        {status === 'error' && (
          <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: '#dc2626' }}><XCircle size={40} /></div>
            <h3 style={{ color: '#dc2626', fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>Merge Failed</h3>
            <p style={{ color: '#b91c1c', marginBottom: 20 }}>{error}</p>
            <button className="btn btn-primary" onClick={handleReset}><RotateCcw size={16} /> Try Again</button>
          </div>
        )}
      </div>

<section className="hero">
      {/* ── SEO Content Block — paste into ConvertPage.js and Home.js ── */}
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>


  {/* ── How it works ──────────────────────────────────────── */}
 <h2>How It <span>Works</span></h2>
 <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16, textAlign: 'left' }}>Convert Any File in 4 Simple Steps</h2>
  <TipsList tips={[
      { label: 'Upload Your File', tip: 'Choose a file from your device or simply drag and drop it. Files up to 50 MB are supported.' },
      { label: 'Choose a Format', tip: 'Pick the format you want. We’ll show the options that work with your file.' },
      { label: 'Click Convert', tip: 'Hit Convert and let the tool do the work. Most files are ready in just a few seconds.' },
      { label: 'Download', tip: 'Download your converted file right away. Your files are automatically deleted after 2 hours.' },
    ]} />
  </div>
</section>
 <section className="section" style={{ background: 'linear-gradient(135deg, #1D3557, #005CFC 55%, #193A6D)' }}>
  <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
      <h2 className="section-title" style={{ color: 'white' }}>All Supported Formats</h2>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { title: 'PDF',    tags: ['PDF → DOCX','PDF → XLSX','PDF → JPG','DOCX → PDF','XLSX → PDF','JPG → PDF'], desc: 'Convert PDFs to editable files or create PDFs from documents and images.' },
      { title: 'Images', tags: ['JPG','PNG','WebP','GIF','BMP','AVIF','ICO'], desc: 'Convert images between popular formats with quality and resize options.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{card.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {card.tags.map(t => (
            <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, border: '1px solid var(--border)', background: '#f8fafc', color: 'var(--text-muted)' }}>{t}</span>
          ))}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section" style={{ background: '#f8fafc' }}>
       
<div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
    <h2 className="section-title">What Makes Our Converter Different</h2>
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { color: '#3B82F6', title: 'No Software Required',   desc: 'Convert your files directly from your browser without installing anything. It works across Windows, Mac, Linux, iPhone, Android, and tablets.' },
      { color: '#22C55E', title: 'Privacy You Can Trust',  desc: 'Your files are securely uploaded using HTTPS and automatically deleted after 2 hours. We never store, share, or use your files for other purposes.' },
      { color: '#F59E0B', title: 'High-Quality Results',   desc: 'Our conversion tools are designed to maintain your file’s quality, formatting, and layout as closely as possible.' },
      { color: '#A855F7', title: 'Free to Use',            desc: 'Convert files up to 50 MB with no watermarks, subscriptions, or hidden charges. Simply upload and convert.' },
      { color: '#005CFC', title: 'Fast & Reliable',        desc: 'Most conversions are completed in just a few seconds, giving you your converted file without unnecessary waiting.' },
      { color: '#0EA5E9', title: 'Easy on Every Device',   desc: 'Whether you’re using a computer, tablet, or smartphone, you can upload, convert, and download your files easily from anywhere.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderLeft: `3px solid ${card.color}`, borderRadius: '0 12px 12px 0', padding: '14px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{card.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.55 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section hero" style={{ background: 'white' }}>
       
 <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
  {/* ── Pro tips ──────────────────────────────────────────── */}

    <h2>Tips for Better <span> Results</span></h2>
   <TipsList tips={[
      // PDF to Word and Compress PDF tips hidden while those tools are off.
      // { label: 'PDF to Word',       tip: 'Use text-based PDFs for cleaner, editable results.' },
      { label: 'Reduce Image Size', tip: 'Convert images to WebP for smaller file sizes and good quality.' },
      { label: 'Images to PDF',     tip: 'Combine multiple images into one PDF for easy sharing.' },
      // { label: 'Compress PDF',      tip: 'Reduce large PDF sizes before emailing or uploading.' },
      { label: 'Fix Page Direction', tip: 'If a scanned page is sideways, rotate it before merging so the final PDF reads cleanly.' },
    ]} />
</div>
</section>
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif', alignItems: 'center',
}}>
  {/* ── FAQ ───────────────────────────────────────────────── */}
     <h2 className="section-title">Frequently Asked Questions</h2>
   <FaqAccordion faqs={[
  {
    q: 'Is the Merge PDF tool free?',
    a: 'Yes, you can merge PDF files for free.'
  },
  {
    q: 'Are my PDF files safe?',
    a: 'Yes. Your files are handled securely and removed after processing.'
  },
  {
    q: 'How many PDFs can I merge?',
    a: 'You can combine multiple PDF files into one.'
  },
  {
    q: 'Can I change the order of the files?',
    a: 'Yes. Simply arrange the PDFs in the order you need before merging.'
  },
  {
    q: 'Will merging affect the PDF quality?',
    a: 'No. Merging keeps the original content and quality of your PDFs.'
  },
  {
    q: 'Can I merge PDFs on my phone?',
    a: 'Yes. You can use the tool on phones, tablets, or computers.'
  }
]} />
      </div>
    </div>
  );
}
